import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Button, Input, Space, Tooltip, Switch } from "antd";
import {
  EyeOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { all_routes } from "../../../router/all_routes";
import { showToast } from "../../../../core/data/redux/slices/ToastSlice";
import api from "../../../../core/axios/axiosInstance";
import { useDebounce } from "../../../../core/hooks/useDebounce";
import {
  setCompaniesPage,
  getCompaniesPage,
  clearCompaniesCache,
  setSearchQuery,
  setCurrentPage,
  setLoading,
  setError,
} from "../../../../core/data/redux/slices/adminCompanySlice";

const ManageCompanies = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get data from Redux
  const { companies, cachedPages, pagination, searchQuery, loading } =
    useSelector((state) => state.adminCompany);

  const [localSearch, setLocalSearch] = useState(searchQuery);
  const debouncedSearch = useDebounce(localSearch, 500);
  const [loadingToggles, setLoadingToggles] = useState({});

  useEffect(() => {
    fetchCompanyAdmins(pagination.current, pagination.pageSize, searchQuery);
    // eslint-disable-next-line
  }, [pagination.current, pagination.pageSize, searchQuery]);

  const fetchCompanyAdmins = async (
    page = 1,
    limit = 10,
    search = "",
    forceRefresh = false
  ) => {
    // Check if page is already cached
    if (cachedPages[page] && search === searchQuery && !forceRefresh) {
      dispatch(getCompaniesPage(page));
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await api.post("/superAdmin/allCompanyAdmin", {
        page,
        limit,
        search,
      });
      if (response.data.status === "success") {
        dispatch(
          setCompaniesPage({
            page,
            data: response.data.data,
            pagination: {
              current: response.data.page,
              pageSize: response.data.limit,
              total: response.data.totalAdmins,
            },
          })
        );
      }
    } catch (error) {
      dispatch(setError(error.message));
      dispatch(
        showToast({
          heading: "Error",
          message: "Failed to fetch company admins",
          variant: "danger",
        })
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleTableChange = (pag) => {
    dispatch(setCurrentPage(pag.current));
  };

  // handleSearch is kept for manual triggering if needed (not used by default)
  const handleSearch = (value) => {
    dispatch(setSearchQuery(value));
    dispatch(clearCompaniesCache());
    fetchCompanyAdmins(1, pagination.pageSize, value);
  };

  // When the debounced input changes, update the global search query and clear cache.
  useEffect(() => {
    // Avoid dispatching if nothing changed
    if (debouncedSearch !== searchQuery) {
      dispatch(setSearchQuery(debouncedSearch));
      dispatch(clearCompaniesCache());
      // fetching is handled by the existing useEffect that listens to searchQuery
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleViewDetails = (admin) => {
    navigate(all_routes.companyDetails, { state: { company: admin } });
  };

  const handleToggleAccountStatus = async (userId, currentStatus) => {
    const toggleKey = `account-${userId}`;
    setLoadingToggles((prev) => ({ ...prev, [toggleKey]: true }));

    try {
      const newStatus = currentStatus === "active" ? "suspended" : "active";
      const endpoint =
        newStatus === "active" ? "/deleteUser/activate" : "/deleteUser/suspend";

      const response = await api.post(endpoint, {
        user_id: userId,
      });

      if (response.data.status === "success") {
        // Allow animation to complete before refreshing
        await new Promise((resolve) => setTimeout(resolve, 800));

        dispatch(
          showToast({
            heading: "Success",
            message: response.data.message,
            variant: "success",
          })
        );

        // dispatch(clearCompaniesCache());
        await fetchCompanyAdmins(
          pagination.current,
          pagination.pageSize,
          searchQuery,
          true
        );
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message || "Failed to update account status";
      dispatch(
        showToast({
          heading: "Error",
          message: msg,
          variant: "danger",
        })
      );
    } finally {
      setLoadingToggles((prev) => {
        const newState = { ...prev };
        delete newState[toggleKey];
        return newState;
      });
    }
  };

  const handleToggleExtensionStatus = async (userId, company, checked) => {
    const toggleKey = `extension-${userId}`;
    setLoadingToggles((prev) => ({ ...prev, [toggleKey]: true }));

    try {
      const response = await api.put("/superAdmin/editCompanyAdminAndAgent", {
        userId: userId,
        extensionNumber: company.extensionNumber,
        telephone: company.telephone,
        sipSecret: company.sipSecret,
        status: checked,
      });

      if (response.data.status === "success") {
        // Allow animation to complete before refreshing
        await new Promise((resolve) => setTimeout(resolve, 800));

        dispatch(
          showToast({
            heading: "Success",
            message: checked
              ? "Extension activated successfully"
              : "Extension deactivated successfully",
            variant: "success",
          })
        );

        // dispatch(clearCompaniesCache());
        await fetchCompanyAdmins(
          pagination.current,
          pagination.pageSize,
          searchQuery,
          true
        );
      }
    } catch (error) {
      const msg =
        error?.response?.data?.error || "Failed to update extension status";
      dispatch(
        showToast({
          heading: "Error",
          message: msg,
          variant: "danger",
        })
      );
    } finally {
      setLoadingToggles((prev) => {
        const newState = { ...prev };
        delete newState[toggleKey];
        return newState;
      });
    }
  };

  const adminColumns = [
    {
      title: "Name",
      key: "name",
      render: (_, record) => (
        <span>
          <UserOutlined style={{ marginRight: 8 }} />
          {record.firstname} {record.lastname}
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => (
        <span>
          <MailOutlined style={{ marginRight: 8 }} />
          {email}
        </span>
      ),
    },
    {
      title: "Company Name",
      dataIndex: "userInfo",
      key: "companyName",
      render: (userInfo) => <span>{userInfo?.companyName || "N/A"}</span>,
    },
    {
      title: "Extension Status",
      dataIndex: "extensionStatus",
      key: "extensionStatus",
      render: (extensionStatus, record) => {
        const toggleKey = `extension-${record._id}`;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Switch
              checked={extensionStatus}
              loading={loadingToggles[toggleKey]}
              onChange={(checked) =>
                handleToggleExtensionStatus(record._id, record, checked)
              }
              checkedChildren={"Active"}
              unCheckedChildren={"Inactive"}
              size="large"
            />
          </div>
        );
      },
    },
    {
      title: "Account Status",
      dataIndex: "accountStatus",
      key: "accountStatus",
      render: (accountStatus, record) => {
        const toggleKey = `account-${record._id}`;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Switch
              checked={accountStatus === "active"}
              loading={loadingToggles[toggleKey]}
              onChange={() =>
                handleToggleAccountStatus(record._id, accountStatus)
              }
              checkedChildren={"Active"}
              unCheckedChildren={"Suspended"}
              size="large"
            />
          </div>
        );
      },
    },
    {
      title: "Extension",
      dataIndex: "extensionNumber",
      key: "extensionNumber",
      render: (ext) => <Tag color="blue">{ext || "N/A"}</Tag>,
    },
    {
      title: "Telephone Number",
      dataIndex: "telephone",
      key: "telephone",
      render: (phone) => (
        <span>
          <PhoneOutlined style={{ marginRight: 8 }} />
          {phone || "N/A"}
        </span>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewDetails(record)}
            >
              View
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-6">
                  <h3 className="page-title">Manage Companies</h3>
                  <ul className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.adminDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">Companies</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-semibold mb-0">All Company Admins</h5>
                  <Space>
                    <Input
                      placeholder="Search by name, email, phone..."
                      value={localSearch}
                      onChange={(e) => setLocalSearch(e.target.value)}
                      style={{ width: 300 }}
                      allowClear
                    />
                  </Space>
                </div>
                <Table
                  columns={adminColumns}
                  dataSource={companies}
                  rowKey="_id"
                  loading={loading}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                  }}
                  onChange={handleTableChange}
                  bordered
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageCompanies;
